import {Search, X, SlidersHorizontal, Calendar} from 'lucide-react'
import {useState} from 'react'
import type {Priority} from '../../types'
import {useApp} from '../../context/AppContext'
import {getPriorityConfig, getStatusConfig} from '../../utils/helpers'
import Avatar from '../common/Avatar'

export interface TaskFilters {
  search: string
  status: string[]
  priority: Priority[]
  assigneeIds: string[]
  boardIds: string[]
  labelIds: string[]
  dueDateRange: 'all' | 'overdue' | 'today' | 'this_week' | 'this_month' | 'no_date'
  hasSubtasks: 'all' | 'yes' | 'no'
}

export const defaultFilters: TaskFilters = {
  search: '',
  status: [],
  priority: [],
  assigneeIds: [],
  boardIds: [],
  labelIds: [],
  dueDateRange: 'all',
  hasSubtasks: 'all',
}

interface TasksFiltersProps {
  filters: TaskFilters
  onChange: (filters: TaskFilters) => void
  allStatuses: string[]
}

const priorities: Priority[] = ['low', 'medium', 'high', 'critical']

const dueDateOptions: {value: TaskFilters['dueDateRange']; label: string}[] = [
  {value: 'all', label: 'Todas'},
  {value: 'overdue', label: 'Atrasadas'},
  {value: 'today', label: 'Hoje'},
  {value: 'this_week', label: 'Esta semana'},
  {value: 'this_month', label: 'Este mês'},
  {value: 'no_date', label: 'Sem prazo'},
]

export default function TasksFilters({filters, onChange, allStatuses}: TasksFiltersProps) {
  const {state} = useApp()
  const [showAdvanced, setShowAdvanced] = useState(false)

  const activeFilterCount = [
    filters.status.length > 0,
    filters.priority.length > 0,
    filters.assigneeIds.length > 0,
    filters.boardIds.length > 0,
    filters.labelIds.length > 0,
    filters.dueDateRange !== 'all',
    filters.hasSubtasks !== 'all',
  ].filter(Boolean).length

  const update = (partial: Partial<TaskFilters>) => {
    onChange({...filters, ...partial})
  }

  const toggleArrayFilter = <K extends keyof TaskFilters>(key: K, value: string) => {
    const current = filters[key] as string[]
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value]
    update({[key]: next} as Partial<TaskFilters>)
  }

  const clearAll = () => onChange(defaultFilters)

  return (
    <div className='devops-filter-panel space-y-3'>
      <div className='flex flex-col sm:flex-row gap-3'>
        <div className='flex-1 relative'>
          <Search size={16} className='absolute left-3 top-1/2 -translate-y-1/2 text-slate-400' />
          <input
            type='text'
            value={filters.search}
            onChange={(e) => update({search: e.target.value})}
            placeholder='Buscar tarefas por título ou descrição...'
            className='w-full pl-10 pr-10 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-300 focus:border-brand-400 transition-all placeholder:text-slate-400'
            aria-label='Buscar tarefas'
          />
          {filters.search && (
            <button
              onClick={() => update({search: ''})}
              className='absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors'
              aria-label='Limpar busca'
            >
              <X size={16} />
            </button>
          )}
        </div>

        <div className='flex gap-2'>
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${
              showAdvanced || activeFilterCount > 0
                ? 'bg-brand-50 border-brand-200 text-brand-600'
                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            <SlidersHorizontal size={16} />
            <span className='hidden sm:inline'>Filtros</span>
            {activeFilterCount > 0 && (
              <span className='inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-500 text-white text-xs font-bold'>
                {activeFilterCount}
              </span>
            )}
          </button>

          {activeFilterCount > 0 && (
            <button
              onClick={clearAll}
              className='inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-red-200 bg-red-50 text-red-600 text-sm font-medium hover:bg-red-100 transition-colors'
            >
              <X size={14} />
              <span className='hidden sm:inline'>Limpar</span>
            </button>
          )}
        </div>
      </div>

      <div className='flex flex-wrap gap-2'>
        {allStatuses.map((s) => {
          const config = getStatusConfig(s)
          const active = filters.status.includes(s)
          return (
            <button
              key={s}
              onClick={() => toggleArrayFilter('status', s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                active
                  ? `${config.color} border-current shadow-sm`
                  : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
              }`}
            >
              {config.label}
            </button>
          )
        })}
      </div>

      {showAdvanced && (
        <div className='bg-slate-50/80 rounded-2xl border border-slate-200 p-4 space-y-4 animate-fade-in'>
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
            <FilterSection label='Prioridade'>
              <div className='flex flex-wrap gap-1.5'>
                {priorities.map((p) => {
                  const config = getPriorityConfig(p)
                  const active = filters.priority.includes(p)
                  return (
                    <button
                      key={p}
                      onClick={() => toggleArrayFilter('priority', p)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all border ${
                        active
                          ? `${config.color} border-current`
                          : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      {config.label}
                    </button>
                  )
                })}
              </div>
            </FilterSection>

            <FilterSection label='Quadro'>
              <div className='flex flex-wrap gap-1.5'>
                {state.boards.map((board) => {
                  const active = filters.boardIds.includes(board.id)
                  return (
                    <button
                      key={board.id}
                      onClick={() => toggleArrayFilter('boardIds', board.id)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all border ${
                        active
                          ? 'bg-brand-50 text-brand-600 border-brand-200'
                          : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      {board.name}
                    </button>
                  )
                })}
              </div>
            </FilterSection>

            <FilterSection label='Prazo'>
              <div className='flex flex-wrap gap-1.5'>
                {dueDateOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => update({dueDateRange: opt.value})}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all border ${
                      filters.dueDateRange === opt.value
                        ? 'bg-brand-50 text-brand-600 border-brand-200'
                        : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    {opt.value === 'overdue' && <Calendar size={12} />}
                    {opt.label}
                  </button>
                ))}
              </div>
            </FilterSection>

            <FilterSection label='Responsável'>
              <div className='flex flex-wrap gap-1.5 max-h-32 overflow-y-auto'>
                {state.users.map((user) => {
                  const active = filters.assigneeIds.includes(user.id)
                  return (
                    <button
                      key={user.id}
                      onClick={() => toggleArrayFilter('assigneeIds', user.id)}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all border ${
                        active
                          ? 'bg-brand-50 text-brand-600 border-brand-200'
                          : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      <Avatar name={user.name} color={user.avatarColor} size='xs' />
                      {user.name.split(' ')[0]}
                    </button>
                  )
                })}
              </div>
            </FilterSection>

            <FilterSection label='Etiquetas'>
              <div className='flex flex-wrap gap-1.5 max-h-32 overflow-y-auto'>
                {state.labels.map((label) => {
                  const active = filters.labelIds.includes(label.id)
                  return (
                    <button
                      key={label.id}
                      onClick={() => toggleArrayFilter('labelIds', label.id)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all border ${
                        active
                          ? 'text-white border-transparent shadow-sm'
                          : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                      style={active ? {backgroundColor: label.color} : undefined}
                    >
                      <span className='flex items-center gap-1.5'>
                        <span
                          className='w-2 h-2 rounded-full shrink-0'
                          style={{backgroundColor: label.color}}
                        />
                        {label.name}
                      </span>
                    </button>
                  )
                })}
              </div>
            </FilterSection>

            <FilterSection label='Subtarefas'>
              <div className='flex gap-1.5'>
                {(
                  [
                    {value: 'all', label: 'Todas'},
                    {value: 'yes', label: 'Com subtarefas'},
                    {value: 'no', label: 'Sem subtarefas'},
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => update({hasSubtasks: opt.value})}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all border ${
                      filters.hasSubtasks === opt.value
                        ? 'bg-brand-50 text-brand-600 border-brand-200'
                        : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </FilterSection>
          </div>
        </div>
      )}
    </div>
  )
}

function FilterSection({label, children}: {label: string; children: React.ReactNode}) {
  return (
    <div>
      <p className='text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2'>{label}</p>
      {children}
    </div>
  )
}
